import {Form, Formik} from 'formik'
import {useEffect, useState} from 'react'
import * as Yup from 'yup'

import InlineTable from 'src/feature/bite/component/InlineTable'

import {useIntl} from 'react-intl'
import {useMerchant} from 'src/feature/store/hook'
import {useUpdateMerchant} from '../../context/useMutation'
import StoreContact from './StoreContact'
import StoreContract from './StoreContract'
import StoreFacility from './StoreFacility'
import StoreSocialMedia from './StoreSocialMedia'
import StoreFinancial from './StoreFinancial'

import getCategories from 'src/feature/become-merchant/services/getCategories'
import getSubCategories from 'src/feature/become-merchant/services/getSubCategories'
import {useGetDetailCorporate} from '../../context/useQuery'
import {ConnectedFocusError} from 'focus-formik-error'
import useValidationSchemaRenderer from './utils/useValidationSchemaRenderer'

const StoreDetailOverview = ({loading}: {loading: boolean}) => {
  const intl = useIntl()

  const [editable, setEditable] = useState({
    overview: false,
    contact: false,
    facility: false,
    socialMedia: false,
    contract: false,
    financial: false,
  })

  const {detail} = useMerchant()
  const {mutateAsync, isLoading} = useUpdateMerchant()
  const {refetch} = useGetDetailCorporate()
  const [categoryList, setCategoryList] = useState([])
  const [subCategoryList, setSubCategoryList] = useState([])
  const [selectedCategoryID, setSelectedCategoryID] = useState(detail?.categoryIDs || [''])
  const {renderer} = useValidationSchemaRenderer()

  const column = [
    [
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.EXTRA_ID'}),
        key: 'merchantID',
        dataIndex: 'merchantID',
        tooltip:
          'A unique system-generated identifier assigned to the corporate account within the Vasco platform.',
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.STATUS'}),
        key: 'status',
        dataIndex: 'status',
        tooltip:
          'Indicates the current status of your business (e.g., Active, Pending, or Inactive).',
        edit: 'select',
        status: true,
        valueSelect: true,
        selects: [
          {value: 1, text: 'Active'},
          {value: 2, text: 'Passive', disabled: true},
        ],
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OPTIONALS.WEB_SITE'}),
        key: 'webSite',
        dataIndex: 'webSite',
        edit: 'input',
        required: true,
        maxLength: 51,
        tooltip: 'The official website of the corporate.',
      },
    ],
    [
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.MERCHANT_NAME'}),
        key: 'officialName',
        dataIndex: 'officialName',
        edit: 'input',
        required: true,
        maxLength: 51,
        tooltip: 'The business name as it appears on the platform.',
      },
      {
        title: 'Category',
        key: 'categoryIDs',
        dataIndex: 'categoryIDs',
        required: true,
        edit: 'multiple_select',
        valueSelect: true,
        tooltip:
          'The primary classification of the business based on its industry or service offerings.',
        selects: categoryList?.map((item: any) => ({value: item.id, text: item.name})) || [],
        setSelectedCategoryID: setSelectedCategoryID,
      },
      {
        title: 'Sub-Category',
        key: 'subCategoryIDs',
        dataIndex: 'subCategoryIDs',
        edit: 'multiple_select',
        tooltip:
          'A more specific classification under the selected category to define the business type.',
        valueSelect: true,
        required: true,
        selects:
          subCategoryList?.map((item: any) => ({
            value: item.id,
            text: item.name,
            merchantCategoryName: item.merchantCategoryName,
            merchantCategoryID: item.merchantCategoryID,
          })) || [],
      },
    ],
  ]

  useEffect(() => {
    if (categoryList.length === 0) {
      getCategories().then((category: any) => {
        setCategoryList(category || [])
      })
    }

    if (!detail?.categoryIDs) return

    getSubCategories({merchantCategoryID: detail?.categoryIDs || ['']}).then((subCategory: any) => {
      setSubCategoryList(subCategory || [])
    })
  }, [detail?.categoryIDs])

  useEffect(() => {
    if (selectedCategoryID) {
      getSubCategories({merchantCategoryID: selectedCategoryID}).then((subCategory: any) => {
        setSubCategoryList(subCategory || [])
      })
    }
  }, [selectedCategoryID])

  return (
    <>
      {detail && (
        <Formik
          initialValues={{
            merchantID: detail?.merchantID || '',
            mccCode: detail?.mccCode || '',
            status: detail?.status || 1,
            addressLine: detail?.addressLine || '',
            zipCode: detail?.zipCode || '',
            categoryIDs: detail?.categoryIDs || [],
            subCategoryIDs: detail?.subCategoryIDs || [],
            countryPhoneCode: '+971',
            culture: 'AE',
            countryID: detail?.countryID || 'f109e594-b301-ec11-a85d-000d3a229408',
            cityID: detail?.cityID || '',
            webSite: detail?.webSite || '',
            agreementDate: detail?.agreementDate || '',
            slipName: detail?.slipName || '',
            slipSlogan: detail?.slipSlogan || '',
            contactName: detail?.contactName || '',
            contactSurname: detail?.contactSurname || '',
            contactPhone: detail?.contactPhone || '',
            contactEmail: detail?.contactEmail || '',
            contactTitle: detail?.contactTitle || '',
            facebook: detail?.facebook || '',
            linkedIn: detail?.linkedIn || '',
            instagram: detail?.instagram || '',
            twitter: detail?.twitter || '',
            contractDate: detail?.contractDate || '',
            onBoardingDate: detail?.onBoardingDate || '',
            officialName: detail?.officialName || '',
            imagesData: detail?.imagesData || [],
            infoContentData: detail?.infoContentData || [],
            financialCurrency: detail?.financialCurrency,
            financialPayoutDate: detail?.financialPayoutDate,
            financialPayoutMethod: detail?.financialPayoutMethod,
            contactDuration: detail?.contactDuration,
            financialPaymentFrequency: detail?.financialPaymentFrequency,
            contractDurationPeriod: detail?.contractDurationPeriod,
            financialOfficialName: detail?.financialOfficialName || '',
            financialBankName: detail?.financialBankName || '',
            financialTaxOffice: detail?.financialTaxOffice || '',
            financialInvoiceAddress: detail?.financialInvoiceAddress || '',
            financialIBAN: detail?.financialIBAN || '',
            financialTaxNumber: detail?.financialTaxNumber || '',
            additionalInformations: detail?.additionalInformations || '',
          }}
          validate={async (values) => {
            const schema = renderer(editable)

            const errors: any = {}
            try {
              await schema.validate(values, {abortEarly: false})
            } catch (err: any) {
              if (err.inner && err.inner.length > 0) {
                err.inner.forEach((error: any) => {
                  if (!errors[error.path]) {
                    errors[error.path] = error.message
                  }
                })
              } else {
                errors[err.path] = err.message
              }
            }
            return errors
          }}
          onSubmit={async (values, {resetForm}) => {
            const updateResult = await mutateAsync({values})

            if (updateResult) {
              const response = (await refetch()) as any
              resetForm({
                values: {
                  ...values,
                  infoContentData: response?.data?.infoContentData || [],
                  additionalInformations: response?.data?.additionalInformations || '',
                },
              })
              setEditable({
                overview: false,
                contact: false,
                facility: false,
                socialMedia: false,
                contract: false,
                financial: false,
              })
            }
          }}
        >
          {({values, resetForm, handleSubmit}) => {
            return (
              <Form>
                <ConnectedFocusError />
                <div className='card mb-5 mb-xl-10'>
                  <div className='card-header'>
                    <div className='card-title m-0'>
                      <div>
                        <h3 className='fw-bold mb-2 mt-5'>
                          {intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW'})}
                        </h3>
                        <p className='text-muted fw-normal fs-6'>
                          {intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW_TOOLTIP'})}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className='card-body p-9'>
                    <InlineTable
                      loading={loading}
                      dataSource={values}
                      columns={column}
                      editable={editable.overview}
                      values={values}
                    />
                  </div>
                  <div className='card-footer d-flex w-100 justify-content-end'>
                    {editable.overview ? (
                      <div>
                        <button
                          className='btn btn-primary'
                          style={{marginRight: 10}}
                          onClick={() => {
                            setEditable({
                              overview: false,
                              contact: false,
                              facility: false,
                              socialMedia: false,
                              contract: false,
                              financial: false,
                            })
                            resetForm()
                          }}
                        >
                          <i className='bi bi-x me-2'></i>
                          {intl.formatMessage({id: 'GLOBAL.BUTTON.CANCEL'})}
                        </button>
                        <button
                          className='btn btn-primary'
                          disabled={isLoading}
                          type='button'
                          onClick={async () => {
                            await handleSubmit()
                          }}
                        >
                          <i className='bi bi-save2 me-2'></i>
                          {isLoading
                            ? intl.formatMessage({id: 'GLOBAL.BUTTON.SAVING'})
                            : intl.formatMessage({id: 'GLOBAL.BUTTON.SAVE'})}
                        </button>
                      </div>
                    ) : (
                      <button
                        className='btn btn-primary'
                        onClick={() => {
                          resetForm()
                          setEditable({
                            overview: true,
                            contact: false,
                            facility: false,
                            socialMedia: false,
                            contract: false,
                            financial: false,
                          })
                        }}
                      >
                        <i className='bi bi-pencil me-2'></i>
                        {intl.formatMessage({id: 'GLOBAL.BUTTON.EDIT'})}
                      </button>
                    )}
                  </div>
                </div>

                <StoreContact
                  intl={intl}
                  loading={isLoading}
                  editable={editable}
                  setEditable={setEditable}
                />

                <StoreFacility
                  intl={intl}
                  loading={isLoading}
                  editable={editable}
                  setEditable={setEditable}
                />

                <StoreSocialMedia
                  intl={intl}
                  loading={isLoading}
                  editable={editable}
                  setEditable={setEditable}
                />

                <StoreContract
                  intl={intl}
                  loading={isLoading}
                  editable={editable}
                  setEditable={setEditable}
                />

                <StoreFinancial
                  intl={intl}
                  loading={isLoading}
                  editable={editable}
                  setEditable={setEditable}
                />
              </Form>
            )
          }}
        </Formik>
      )}
    </>
  )
}

export default StoreDetailOverview
